import axios from "axios";

interface LogEntry {
  labels: Record<string, string>;
  entries: Array<{
    ts?: number;
    line: string;
  }>;
}

interface Stream {
  labels: Record<string, string>;
  entries: Array<{
    ts?: number;
    line: string;
  }>;
}

interface Batch {
  streams: Stream[];
}

interface BatcherOptions {
  host: string;
  path?: string;
  headers?: Record<string, string>;
  /** @default 5000 (milliseconds) */
  interval?: number;
  batching?: boolean;
  timeout?: number;
  replaceTimestamp?: boolean;
  clearOnError?: boolean;
  gracefulShutdown?: boolean;
}

/**
 * A batching transport layer for Grafana Loki
 * @link A leaner Typescript version of https://github.com/scumpah/winston-loki/blob/v6.0.0/src/batcher.js
 *
 * @description Remove snappy usage and force axios with JSON only requests
 */
class Batcher {
  private options: BatcherOptions;
  private url: URL;
  interval: number;
  circuitBreakerInterval: number;
  batch: Batch;
  private contentType: string;
  runLoop: boolean = false;

  /**
   * Creates an instance of Batcher.
   * Starts the batching loop if enabled.
   */
  constructor(options: BatcherOptions) {
    // Load given options to the object
    this.options = options;

    // Construct Grafana Loki push API url
    const path = this.options.path || "/loki/api/v1/push";
    this.url = new URL(this.options.host + path);

    // Define the batching intervals
    this.interval = this.options.interval
      ? Number(this.options.interval)
      : 5000;
    this.circuitBreakerInterval = 60000;

    // Initialize the log batch
    this.batch = {
      streams: [],
    };

    // Define the content type headers for the POST request based on the data type
    this.contentType = "application/json";

    // If batching is enabled, run the loop
    this.options.batching && this.run();

    if (this.options.gracefulShutdown) {
      // Handle graceful shutdown without async-exit-hook dependency
      const handleExit = () => {
        this.close();
      };
      process.on("beforeExit", handleExit);
      process.on("SIGINT", handleExit);
      process.on("SIGTERM", handleExit);
    }
  }

  /**
   * Returns a promise that resolves after the given duration.
   */
  wait(duration: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, duration);
    });
  }

  /**
   * Pushes logs into the batch.
   * If logEntry is given, pushes it straight to this.sendBatchToLoki()
   */
  async pushLogEntry(logEntry: LogEntry): Promise<void> {
    const noTimestamp =
      logEntry && logEntry.entries && logEntry.entries[0].ts === undefined;

    // If user has decided to replace the given timestamps with a generated one, generate it
    if (this.options.replaceTimestamp || noTimestamp) {
      logEntry.entries[0].ts = Date.now();
    }

    // If batching is not enabled, push the log immediately to Loki API
    if (this.options.batching !== undefined && !this.options.batching) {
      await this.sendBatchToLoki(logEntry);
    } else {
      const { streams } = this.batch;

      // Find if there's already a log with identical labels in the batch
      const match = streams.findIndex(
        (stream) =>
          JSON.stringify(stream.labels) === JSON.stringify(logEntry.labels)
      );

      if (match > -1) {
        // If there's a match, push the log under the same label
        logEntry.entries.forEach((entry) => {
          streams[match].entries.push(entry);
        });
      } else {
        // Otherwise, create a new label under streams
        streams.push(logEntry);
      }
    }
  }

  /**
   * Clears the batch.
   */
  clearBatch(): void {
    this.batch.streams = [];
  }

  /**
   * Prepares JSON batch for sending to Loki
   */
  prepareJSONBatch(batch: Batch): Batch {
    batch.streams = batch.streams.map((logEntry) => {
      const stream: any = logEntry;
      stream.stream = logEntry.labels;
      stream.values = logEntry.entries;
      stream.values = stream.values.map((entry: any) => {
        return [JSON.stringify(entry.ts * 1000 * 1000), entry.line];
      });
      delete stream.entries;
      delete stream.labels;
      return stream;
    });
    return batch;
  }

  /**
   * Sends a batch to Grafana Loki push endpoint.
   * If a single logEntry is given, creates a batch first around it.
   */
  sendBatchToLoki(logEntry?: LogEntry): Promise<void> {
    return new Promise((resolve, reject) => {
      // If the batch is empty, do nothing
      if (this.batch.streams.length === 0 && !logEntry) {
        resolve();
        return;
      }

      let reqBody: string;

      // Prepare JSON batch
      let preparedJSONBatch: Batch;
      if (logEntry !== undefined) {
        // If a single logEntry is given, wrap it according to the batch format
        preparedJSONBatch = this.prepareJSONBatch({ streams: [logEntry] });
      } else {
        // Stringify the JSON ready for transport
        preparedJSONBatch = this.prepareJSONBatch(this.batch);
      }
      reqBody = JSON.stringify(preparedJSONBatch);

      // Send the data to Grafana Loki using axios
      axios
        .post(this.url.toString(), reqBody, {
          headers: {
            ...this.options.headers,
            "Content-Type": this.contentType,
          },
          timeout: this.options.timeout || 5000,
        })
        .then(() => {
          // No need to clear the batch if batching is disabled
          if (logEntry === undefined) {
            this.clearBatch();
          }
          resolve();
        })
        .catch((err) => {
          // Clear the batch on error if enabled
          if (this.options.clearOnError) {
            this.clearBatch();
          }
          reject(err);
        });
    });
  }

  /**
   * Runs the batch push loop.
   *
   * Sends the batch to Loki and waits for
   * the amount of this.interval between requests.
   */
  async run(): Promise<void> {
    this.runLoop = true;
    while (this.runLoop) {
      try {
        await this.sendBatchToLoki();
        if (this.interval === this.circuitBreakerInterval) {
          if (this.options.interval !== undefined) {
            this.interval = Number(this.options.interval);
          } else {
            this.interval = 5000;
          }
        }
      } catch (e) {
        this.interval = this.circuitBreakerInterval;
      }
      await this.wait(this.interval);
    }
  }

  /**
   * Stops the batch push loop
   */
  close(callback?: () => void): void {
    this.runLoop = false;
    this.sendBatchToLoki()
      .then(() => {
        callback?.();
      })
      .catch(() => {
        callback?.();
      });
  }
}

export default Batcher;
